import React, { useState } from "react";
import { useAuth } from "contexts/AuthProvider";
import { axiosInstance } from "contexts/axiosApiInstance";
import { OrgBanner } from "components/main/organizations/OrgBanner";
import { formatDayMonthYear, toUtcWall } from "@/lib/dates";
import { ConsentModal } from "./ConsentModal";
import { tos } from "./legalContent";
import { useConsentStage } from "./consentStage";
import { useTermsPromptStore } from "@/stores/useTermsPromptStore";

const DISMISSED_KEY = "termsBannerDismissedOn";

function today(): string {
  return new Date().toDateString();
}

function dismissedToday(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === today();
  } catch {
    return false;
  }
}

/// Changed Terms during their grace period, or a changed Privacy Policy: a
/// line above the page, never a wall. "Later" hides it until tomorrow;
/// `TermsReviewRow` in Account settings stays the way back. It renders the one
/// dialog every Review and every `terms_not_accepted` refusal opens.
const reviewMessage = (ppAccepted: boolean) =>
  `We've updated our Terms of Service${ppAccepted ? "" : " and Privacy Policy"}. Please review and accept ${ppAccepted ? "it" : "them"} before ${formatDayMonthYear(toUtcWall(tos.acceptBy))}.`;

export const TermsBanner: React.FC = () => {
  const { ppAccepted, setPpAccepted } = useAuth();
  const stage = useConsentStage();
  const [dismissed, setDismissed] = useState(dismissedToday);
  const { open, reason, ask, close } = useTermsPromptStore();

  if (stage !== "review" && stage !== "notice") return null;

  const later = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, today());
    } catch {
      // Without storage it hides for this visit only.
    }
    setDismissed(true);
  };

  const acknowledge = async () => {
    try {
      await axiosInstance.post("/action/auth/accept-consent", {
        consent_types: ["privacy_policy"],
      });
      setPpAccepted(true);
    } catch {
      // Stays up; the next visit asks again.
    }
  };

  const message =
    stage === "review"
      ? reviewMessage(ppAccepted)
      : "We've updated our Privacy Policy.";

  return (
    <>
      {!dismissed && (
        <OrgBanner
          tone="notice"
          action={{
            label: stage === "review" ? "Review" : "Read",
            onClick: () => ask(),
          }}
          dismiss={
            stage === "review"
              ? { label: "Later", onClick: later }
              : { label: "Got it", onClick: acknowledge }
          }
        >
          {message}
        </OrgBanner>
      )}
      {open && <ConsentModal reason={reason} onClose={close} />}
    </>
  );
};

export const TermsReviewRow: React.FC = () => {
  const { ppAccepted } = useAuth();
  const ask = useTermsPromptStore((s) => s.ask);
  if (useConsentStage() !== "review") return null;

  // The banner's own look, as a card in Account settings rather than a line above the page.
  return (
    <OrgBanner
      tone="notice"
      className="rounded-app! border-b-0!"
      action={{ label: "Review", onClick: () => ask() }}
    >
      {reviewMessage(ppAccepted)}
    </OrgBanner>
  );
};
